import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, IconButton, CircularProgress, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Button, MenuItem, Checkbox, Tooltip, useMediaQuery, useTheme
} from '@mui/material';
import { ArrowBack, Add, Delete, LibraryBooks, CopyAll, Lock, LockOpen, WarningAmber, AutoFixHigh } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { SUBJECT_PATHWAYS, COMMON } from '../constants/pathways';

const API = process.env.REACT_APP_API_URL;

const StageLearningAreaList = () => {
  const navigate = useNavigate();
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [years,       setYears]       = useState([]);
  const [stages,      setStages]      = useState([]);
  const [masterAreas, setMasterAreas] = useState([]);
  const [assigned,    setAssigned]    = useState([]);
  const [yearSel,     setYearSel]     = useState('');
  const [stageSel,    setStageSel]    = useState('');
  const [loading,     setLoading]     = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [error,       setError]       = useState('');
  const [success,     setSuccess]     = useState('');
  const [gradingSystems, setGradingSystems] = useState([]); // [{ system, category, bandCount }]
  const [regrading,   setRegrading]   = useState(false);

  // Assign dialog
  const [assignOpen,  setAssignOpen]  = useState(false);
  const [toAdd,       setToAdd]       = useState([]); // [{ areaCode, areaName, isMandatory }]
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: assignOpen, value: toAdd, onClose: () => setAssignOpen(false) });

  // Copy-year dialog
  const [copyOpen,    setCopyOpen]    = useState(false);
  const [copyFrom,    setCopyFrom]    = useState('');
  const [copying,     setCopying]     = useState(false);

  // Copy-stage dialog
  const [copyStageOpen, setCopyStageOpen] = useState(false);
  const [copyFromStage, setCopyFromStage] = useState('');
  const [copyingStage,  setCopyingStage]  = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [confirmDlg,  setConfirmDlg]  = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    (async () => {
      try {
        const [yRes, sRes, aRes] = await Promise.all([
          axios.get(`${API}/api/academics/years`,          { headers }),
          axios.get(`${API}/api/stages`,                   { headers }),
          axios.get(`${API}/api/academics/learning-areas`, { headers }),
        ]);
        setYears(yRes.data);
        setStages(sRes.data);
        setMasterAreas(aRes.data.filter(a => a.isActive));
        // Pre-select active year
        const active = yRes.data.find(y => y.isActive);
        if (active) setYearSel(active.year);
      } catch { setError('Failed to load setup data'); }
      finally { setInitLoading(false); }
    })();
    // Grading systems for the per-subject assignment dropdown (non-fatal).
    axios.get(`${API}/api/academics/grading-systems`, { headers })
      .then(r => setGradingSystems(r.data || [])).catch(() => {});
  }, []); // eslint-disable-line

  const fetchAssigned = useCallback(async () => {
    if (!yearSel || !stageSel) { setAssigned([]); return; }
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/api/academics/stage-learning-areas?year=${yearSel}&stageCode=${stageSel}`,
        { headers }
      );
      setAssigned(res.data);
    } catch { setError('Failed to load assignments'); }
    finally { setLoading(false); }
  }, [yearSel, stageSel]); // eslint-disable-line

  useEffect(() => { fetchAssigned(); }, [fetchAssigned]);

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 2500);
    return () => clearTimeout(t);
  }, [success]);

  // Areas not yet assigned
  const unassigned = masterAreas.filter(a => !assigned.find(x => x.areaCode === a.areaCode));

  // ── Toggle mandatory ──────────────────────────────────────────────────────
  const handleToggleMandatory = async (item) => {
    try {
      await axios.put(
        `${API}/api/academics/stage-learning-areas/${item._id}`,
        { isMandatory: !item.isMandatory },
        { headers }
      );
      setAssigned(prev => prev.map(a => a._id === item._id ? { ...a, isMandatory: !a.isMandatory } : a));
    } catch { setError('Failed to update'); }
  };

  // ── Pathway (Common / STEM / Social Sciences / Arts & Sports Science) ──────
  const handleSetGradingSystem = async (item, gradingSystem) => {
    try {
      await axios.put(`${API}/api/academics/stage-learning-areas/${item._id}`, { gradingSystem }, { headers });
      setAssigned(prev => prev.map(a => a._id === item._id ? { ...a, gradingSystem } : a));
    } catch { setError('Failed to update grading system'); }
  };

  // Re-stamp already-saved scores for this stage/year to each subject's current
  // grading system (after reassigning systems or editing their bands).
  const handleRegrade = async () => {
    if (!yearSel || !stageSel) return;
    setRegrading(true); setError('');
    try {
      const { data } = await axios.post(`${API}/api/academics/assessment-scores/regrade`,
        { academicYear: yearSel, stageCode: stageSel }, { headers });
      setSuccess(`Regraded ${data.updated} of ${data.scanned} score${data.scanned !== 1 ? 's' : ''} for ${stageName(stageSel)} · ${yearSel}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Regrade failed');
    } finally { setRegrading(false); }
  };

  const handleSetPathway = async (item, pathway) => {
    try {
      await axios.put(`${API}/api/academics/stage-learning-areas/${item._id}`, { pathway }, { headers });
      setAssigned(prev => prev.map(a => a._id === item._id ? { ...a, pathway } : a));
    } catch { setError('Failed to update pathway'); }
  };

  // ── Examinable (false = mandatory/timetabled but not scored, e.g. Group Work)
  const handleToggleExaminable = async (item) => {
    const next = item.isExaminable === false; // toggling → becomes examinable
    try {
      await axios.put(`${API}/api/academics/stage-learning-areas/${item._id}`, { isExaminable: next }, { headers });
      setAssigned(prev => prev.map(a => a._id === item._id ? { ...a, isExaminable: next } : a));
    } catch { setError('Failed to update'); }
  };

  // ── Report column order (sequence) ────────────────────────────────────────
  const setSeqLocal = (id, val) =>
    setAssigned(prev => prev.map(a => a._id === id ? { ...a, sequence: val } : a));

  const saveSequence = async (item, raw) => {
    const n = parseInt(raw, 10);
    const sequence = Number.isFinite(n) ? n : 999;
    try {
      await axios.put(`${API}/api/academics/stage-learning-areas/${item._id}`, { sequence }, { headers });
      setAssigned(prev => prev.map(a => a._id === item._id ? { ...a, sequence } : a)
        .sort((a, b) => (a.sequence ?? 999) - (b.sequence ?? 999) || a.areaCode.localeCompare(b.areaCode)));
      setSuccess('Order updated');
    } catch { setError('Failed to update sequence'); }
  };

  // ── Remove ────────────────────────────────────────────────────────────────
  const handleRemove = (item) => setConfirmDlg(item);

  const confirmDelete = async () => {
    const item = confirmDlg;
    setConfirmDlg(null);
    try {
      await axios.delete(`${API}/api/academics/stage-learning-areas/${item._id}`, { headers });
      setAssigned(prev => prev.filter(a => a._id !== item._id));
      setSuccess(`"${item.areaName}" removed`);
    } catch { setError('Remove failed'); }
  };

  // ── Assign dialog ─────────────────────────────────────────────────────────
  const openAssign = () => {
    setToAdd(unassigned.map(a => ({
      areaCode: a.areaCode, areaName: a.areaName,
      isMandatory: true, pathway: COMMON, isExaminable: true, checked: false
    })));
    setAssignOpen(true);
  };

  const toggleCheck = (code) =>
    setToAdd(prev => prev.map(a => a.areaCode === code ? { ...a, checked: !a.checked } : a));

  const toggleMandatoryNew = (code) =>
    setToAdd(prev => prev.map(a => a.areaCode === code ? { ...a, isMandatory: !a.isMandatory } : a));

  const setPathwayNew = (code, pathway) =>
    setToAdd(prev => prev.map(a => a.areaCode === code ? { ...a, pathway } : a));

  const handleAssign = async () => {
    const selected = toAdd.filter(a => a.checked);
    if (selected.length === 0) { setError('Select at least one learning area'); return; }
    setSaving(true); setError('');
    try {
      const res = await axios.post(
        `${API}/api/academics/stage-learning-areas/bulk`,
        { academicYear: yearSel, stageCode: stageSel, areas: selected },
        { headers }
      );
      setAssignOpen(false);
      fetchAssigned();
      setSuccess(`${res.data.added} area${res.data.added !== 1 ? 's' : ''} assigned`);
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  // ── Copy-year dialog ──────────────────────────────────────────────────────
  const handleCopyYear = async () => {
    if (!copyFrom) { setError('Select a source year'); return; }
    setCopying(true); setError('');
    try {
      const res = await axios.post(
        `${API}/api/academics/stage-learning-areas/copy-year`,
        { fromYear: copyFrom, toYear: yearSel, stageCode: stageSel || undefined },
        { headers }
      );
      setCopyOpen(false);
      fetchAssigned();
      setSuccess(`${res.data.copied} assignment${res.data.copied !== 1 ? 's' : ''} copied from ${copyFrom}` +
        (res.data.skipped > 0 ? ` · ${res.data.skipped} skipped` : ''));
    } catch (err) {
      setError(err.response?.data?.error || 'Copy failed');
    } finally { setCopying(false); }
  };

  // ── Copy-stage dialog ─────────────────────────────────────────────────────
  const handleCopyStage = async () => {
    if (!copyFromStage) { setError('Select a source stage'); return; }
    setCopyingStage(true); setError('');
    try {
      const res = await axios.post(
        `${API}/api/academics/stage-learning-areas/copy-stage`,
        { year: yearSel, fromStage: copyFromStage, toStage: stageSel },
        { headers }
      );
      setCopyStageOpen(false);
      fetchAssigned();
      setSuccess(`${res.data.copied} subject${res.data.copied !== 1 ? 's' : ''} copied from ${stageName(copyFromStage)}` +
        (res.data.skipped > 0 ? ` · ${res.data.skipped} already present` : ''));
    } catch (err) {
      setError(err.response?.data?.error || 'Copy failed');
    } finally { setCopyingStage(false); }
  };

  const stageName = (code) => stages.find(s => s.code === code)?.name || code;

  if (initLoading) return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Loading…</Typography>
    </Box>
  );

  const checkedCount = toAdd.filter(a => a.checked).length;

  // Per-subject controls, shared by the desktop table and the mobile card list.
  const seqField = (item) => (
    <TextField
      type="number" size="small"
      value={item.sequence ?? ''}
      onChange={e => setSeqLocal(item._id, e.target.value)}
      onBlur={e => saveSequence(item, e.target.value)}
      inputProps={{ min: 1, style: { width: 44, textAlign: 'center', padding: '4px 2px' } }}
    />
  );
  const mandatoryChip = (item) => (
    <Tooltip title={item.isMandatory ? 'Click to make Optional' : 'Click to make Mandatory'}>
      <Chip
        icon={item.isMandatory ? <Lock fontSize="small" /> : <LockOpen fontSize="small" />}
        label={item.isMandatory ? 'Mandatory' : 'Optional'} size="small"
        color={item.isMandatory ? 'primary' : 'default'}
        variant={item.isMandatory ? 'filled' : 'outlined'}
        onClick={() => handleToggleMandatory(item)} sx={{ cursor: 'pointer' }}
      />
    </Tooltip>
  );
  const pathwaySelect = (item, full) => (
    <TextField
      select size="small" value={item.pathway || COMMON}
      onChange={e => handleSetPathway(item, e.target.value)}
      SelectProps={{ sx: { fontSize: 13 } }} sx={{ minWidth: 150 }} fullWidth={!!full}
    >
      {SUBJECT_PATHWAYS.map(p => <MenuItem key={p} value={p} sx={{ fontSize: 13 }}>{p}</MenuItem>)}
    </TextField>
  );
  const examinableChip = (item) => (
    <Tooltip title={item.isExaminable === false ? 'Not examinable — click to make Examinable' : 'Examinable — click to mark Not examinable'}>
      <Chip
        label={item.isExaminable === false ? 'Not examinable' : 'Examinable'} size="small"
        color={item.isExaminable === false ? 'default' : 'success'}
        variant={item.isExaminable === false ? 'outlined' : 'filled'}
        onClick={() => handleToggleExaminable(item)} sx={{ cursor: 'pointer' }}
      />
    </Tooltip>
  );
  const gradingSelect = (item, full) => (
    <TextField
      select size="small"
      value={gradingSystems.some(g => g.system === item.gradingSystem) ? item.gradingSystem : ''}
      onChange={e => handleSetGradingSystem(item, e.target.value)}
      SelectProps={{ sx: { fontSize: 13 } }} sx={{ minWidth: 160 }} fullWidth={!!full}
    >
      <MenuItem value="" sx={{ fontSize: 13 }}><em>Stage default</em></MenuItem>
      {gradingSystems.map(g => (
        <MenuItem key={g.system} value={g.system} sx={{ fontSize: 13 }}>
          {g.system}{g.category ? ` · ${g.category}` : ''}
        </MenuItem>
      ))}
    </TextField>
  );
  const removeBtn = (item) => (
    <Tooltip title="Remove from this stage">
      <IconButton size="small" color="error" onClick={() => handleRemove(item)}>
        <Delete fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: { xs: 2, sm: 4 }, px: { xs: 1, sm: 0 } }}>
      <Card elevation={3}>
        <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>

          {/* Header */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <LibraryBooks color="primary" />
              <Box>
                <Typography variant="h5" color="primary">Stage Subjects</Typography>
                <Typography variant="body2" color="text.secondary">
                  Assign learning areas to each stage per academic year
                </Typography>
              </Box>
            </Box>
            <Box display="flex" gap={1} alignItems="center">
              {yearSel && (
                <Button size="small" variant="outlined" startIcon={<CopyAll />}
                  onClick={() => { setCopyFrom(''); setError(''); setCopyOpen(true); }}>
                  Copy from Year
                </Button>
              )}
              <IconButton onClick={() => navigate('/academics')} title="Back to Academics">
                <ArrowBack />
              </IconButton>
            </Box>
          </Box>

          {/* Year + Stage selectors */}
          <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
            <TextField select size="small" label="Academic Year" value={yearSel}
              onChange={e => { setYearSel(e.target.value); setStageSel(''); }} sx={{ minWidth: 160 }}>
              <MenuItem value=""><em>Select Year</em></MenuItem>
              {years.map(y => (
                <MenuItem key={y.year} value={y.year}>
                  {y.year}{y.isActive ? ' (Active)' : ''}
                </MenuItem>
              ))}
            </TextField>

            <TextField select size="small" label="Stage" value={stageSel}
              onChange={e => setStageSel(e.target.value)} sx={{ minWidth: 200 }}
              disabled={!yearSel}>
              <MenuItem value=""><em>Select Stage</em></MenuItem>
              {stages.map(s => <MenuItem key={s.code} value={s.code}>{s.code} — {s.name}</MenuItem>)}
            </TextField>

            {yearSel && stageSel && (
              <Button variant="contained" size="small" startIcon={<Add />} onClick={openAssign}
                disabled={unassigned.length === 0}>
                Assign Subjects
              </Button>
            )}

            {yearSel && stageSel && (
              <Button size="small" variant="outlined" startIcon={<CopyAll />}
                onClick={() => { setCopyFromStage(''); setError(''); setCopyStageOpen(true); }}>
                Copy from Stage
              </Button>
            )}

            {yearSel && stageSel && (
              <Tooltip title="Re-stamp saved scores for this stage/year to each subject's current grading system. Run after changing a subject's grading system or its bands.">
                <span>
                  <Button size="small" variant="outlined" color="secondary"
                    startIcon={regrading ? <CircularProgress size={14} color="inherit" /> : <AutoFixHigh />}
                    onClick={handleRegrade} disabled={regrading}>
                    {regrading ? 'Regrading…' : 'Regrade Scores'}
                  </Button>
                </span>
              </Tooltip>
            )}

            {yearSel && stageSel && (
              <Typography variant="caption" color="text.secondary" ml="auto">
                {assigned.length} subject{assigned.length !== 1 ? 's' : ''} assigned
                {unassigned.length > 0 ? ` · ${unassigned.length} available to add` : ' (all assigned)'}
              </Typography>
            )}
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

          {/* Instructions when no filter */}
          {(!yearSel || !stageSel) && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <LibraryBooks sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
              <Typography color="text.secondary">
                Select an academic year and stage to view or manage subject assignments.
              </Typography>
            </Box>
          )}

          {/* Loading */}
          {yearSel && stageSel && loading && (
            <Box textAlign="center" py={3}><CircularProgress size={28} /></Box>
          )}

          {/* Assignments table */}
          {yearSel && stageSel && !loading && (
            <>
              {assigned.length === 0 ? (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    No subjects assigned to {stageName(stageSel)} for {yearSel} yet.
                  </Typography>
                  <Button sx={{ mt: 1 }} startIcon={<Add />} onClick={openAssign}
                    disabled={unassigned.length === 0}>
                    Assign Subjects
                  </Button>
                </Box>
              ) : isMobile ? (
                /* Mobile: one card per subject (the wide table won't fit) */
                <Box display="flex" flexDirection="column" gap={1.2}>
                  {assigned.map(item => (
                    <Card key={item._id} variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Chip label={item.areaCode} size="small" variant="outlined" />
                          <Typography variant="body2" fontWeight={700} sx={{ flex: 1, minWidth: 0 }}>
                            {item.areaName}
                          </Typography>
                          {removeBtn(item)}
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1.5, rowGap: 1, alignItems: 'center' }}>
                          <Typography variant="caption" color="text.secondary">Seq</Typography>
                          <Box>{seqField(item)}</Box>
                          <Typography variant="caption" color="text.secondary">Type</Typography>
                          <Box>{mandatoryChip(item)}</Box>
                          <Typography variant="caption" color="text.secondary">Examinable</Typography>
                          <Box>{examinableChip(item)}</Box>
                          <Typography variant="caption" color="text.secondary">Pathway</Typography>
                          <Box>{pathwaySelect(item, true)}</Box>
                          <Typography variant="caption" color="text.secondary">Grading</Typography>
                          <Box>{gradingSelect(item, true)}</Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 820 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell align="center" sx={{ width: 72 }}>
                        <Tooltip title="Column order on the Merit List / reports (lower = first)">
                          <strong>Seq</strong>
                        </Tooltip>
                      </TableCell>
                      <TableCell><strong>Code</strong></TableCell>
                      <TableCell><strong>Learning Area / Subject</strong></TableCell>
                      <TableCell align="center"><strong>Type</strong></TableCell>
                      <TableCell align="center" sx={{ minWidth: 170 }}>
                        <Tooltip title="Common = every learner takes it. STEM / Social Sciences / Arts & Sports = only learners on that pathway (drives term registration).">
                          <strong>Pathway</strong>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Non-examinable subjects (e.g. Group Work) are timetabled but excluded from scores and the merit list.">
                          <strong>Examinable</strong>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center" sx={{ minWidth: 180 }}>
                        <Tooltip title="Grading system used to grade this subject's scores. Leave as 'Stage default' to use the stage's curriculum (CBC / 8-4-4).">
                          <strong>Grading System</strong>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {assigned.map(item => (
                      <TableRow key={item._id} hover>
                        <TableCell align="center">{seqField(item)}</TableCell>
                        <TableCell>
                          <Chip label={item.areaCode} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell><strong>{item.areaName}</strong></TableCell>
                        <TableCell align="center">{mandatoryChip(item)}</TableCell>
                        <TableCell align="center">{pathwaySelect(item)}</TableCell>
                        <TableCell align="center">{examinableChip(item)}</TableCell>
                        <TableCell align="center">{gradingSelect(item)}</TableCell>
                        <TableCell align="center">{removeBtn(item)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ── Confirm Remove dialog ── */}
      <Dialog open={Boolean(confirmDlg)} onClose={() => setConfirmDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmber color="error" /> Confirm Remove
        </DialogTitle>
        <DialogContent>
          <Typography>Remove <strong>{confirmDlg?.areaName}</strong> from stage {stageSel} ({yearSel})? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>Remove</Button>
        </DialogActions>
      </Dialog>

      {/* ── Assign Subjects Dialog ── */}
      {discardDialog}
      <Dialog open={assignOpen} onClose={attemptClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Assign Subjects — {stageName(stageSel)} · {yearSel}
        </DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
          {toAdd.length === 0 ? (
            <Typography color="text.secondary">All learning areas are already assigned.</Typography>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Select subjects to assign. Toggle the lock to set Mandatory / Optional, and pick a Pathway
                (Common = all learners).
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell padding="checkbox" />
                    <TableCell><strong>Subject</strong></TableCell>
                    <TableCell align="center"><strong>Mandatory</strong></TableCell>
                    <TableCell align="center"><strong>Pathway</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {toAdd.map(a => (
                    <TableRow key={a.areaCode} hover sx={{ cursor: 'pointer' }}
                      onClick={() => toggleCheck(a.areaCode)}>
                      <TableCell padding="checkbox">
                        <Checkbox size="small" checked={a.checked} onChange={() => {}} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2"><strong>{a.areaName}</strong></Typography>
                        <Typography variant="caption" color="text.secondary">{a.areaCode}</Typography>
                      </TableCell>
                      <TableCell align="center" onClick={e => e.stopPropagation()}>
                        <Tooltip title={a.isMandatory ? 'Mandatory — click to make Optional' : 'Optional — click to make Mandatory'}>
                          <Chip
                            icon={a.isMandatory ? <Lock fontSize="small" /> : <LockOpen fontSize="small" />}
                            label={a.isMandatory ? 'Mandatory' : 'Optional'}
                            size="small"
                            color={a.isMandatory ? 'primary' : 'default'}
                            variant={a.isMandatory ? 'filled' : 'outlined'}
                            onClick={() => toggleMandatoryNew(a.areaCode)}
                            sx={{ cursor: 'pointer' }}
                            disabled={!a.checked}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center" onClick={e => e.stopPropagation()}>
                        <TextField
                          select size="small"
                          value={a.pathway || COMMON}
                          onChange={e => setPathwayNew(a.areaCode, e.target.value)}
                          disabled={!a.checked}
                          SelectProps={{ sx: { fontSize: 12 } }}
                          sx={{ minWidth: 140 }}
                        >
                          {SUBJECT_PATHWAYS.map(p => (
                            <MenuItem key={p} value={p} sx={{ fontSize: 12 }}>{p}</MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                {checkedCount} of {toAdd.length} selected
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleAssign}
            disabled={saving || checkedCount === 0}>
            {saving ? 'Saving…' : `Assign ${checkedCount > 0 ? checkedCount : ''} Subject${checkedCount !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Copy from Year Dialog ── */}
      <Dialog open={copyOpen} onClose={() => { if (!copying) setCopyOpen(false); }} maxWidth="xs" fullWidth>
        <DialogTitle component="div">
          <Box display="flex" alignItems="center" gap={1}>
            <CopyAll color="primary" />
            <Typography variant="h6">Copy Subject Assignments</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Copy subject assignments from a previous year into <strong>{yearSel}</strong>.
            {stageSel ? ` Only ${stageName(stageSel)} will be copied.` : ' All stages will be copied.'}
            {' '}Existing assignments are skipped.
          </Typography>
          <TextField select size="small" label="Copy FROM (source year)" fullWidth
            value={copyFrom} onChange={e => setCopyFrom(e.target.value)}>
            <MenuItem value=""><em>Select source year</em></MenuItem>
            {years.filter(y => y.year !== yearSel).map(y => (
              <MenuItem key={y.year} value={y.year}>{y.year}</MenuItem>
            ))}
          </TextField>
          {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyOpen(false)} disabled={copying}>Cancel</Button>
          <Button variant="contained" startIcon={<CopyAll />}
            onClick={handleCopyYear} disabled={copying || !copyFrom}>
            {copying ? 'Copying…' : 'Copy'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Copy from Stage Dialog ── */}
      <Dialog open={copyStageOpen} onClose={() => { if (!copyingStage) setCopyStageOpen(false); }} maxWidth="xs" fullWidth>
        <DialogTitle component="div">
          <Box display="flex" alignItems="center" gap={1}>
            <CopyAll color="primary" />
            <Typography variant="h6">Copy Subjects from Stage</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Copy all subject assignments from another stage into <strong>{stageName(stageSel)}</strong> for {yearSel}.
            {' '}Subjects already assigned to {stageName(stageSel)} are skipped.
          </Typography>
          <TextField select size="small" label="Copy FROM (source stage)" fullWidth
            value={copyFromStage} onChange={e => setCopyFromStage(e.target.value)}>
            <MenuItem value=""><em>Select source stage</em></MenuItem>
            {stages.filter(s => s.code !== stageSel).map(s => (
              <MenuItem key={s.code} value={s.code}>{s.code} — {s.name}</MenuItem>
            ))}
          </TextField>
          {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyStageOpen(false)} disabled={copyingStage}>Cancel</Button>
          <Button variant="contained" startIcon={<CopyAll />}
            onClick={handleCopyStage} disabled={copyingStage || !copyFromStage}>
            {copyingStage ? 'Copying…' : 'Copy'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StageLearningAreaList;
